import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isLoginCompanySelection } from '@carrierbridge/shared';
import type {
  IUser,
  ICompany,
  IInvitation,
  ICompanyFeatures,
  ILoginCompanySelection,
  IUserPreferences,
  FeatureKey,
} from '@carrierbridge/shared';
import api, { clearSession, getAccessToken, getRefreshToken, setSession } from '@/lib/api';

const AUTH_KEY = ['auth', 'me'] as const;
const USERS_KEY = ['auth', 'users'] as const;
const COMPANIES_KEY = ['auth', 'companies'] as const;
const COMPANY_USERS_KEY = ['auth', 'company-users'] as const;
const COMPANY_INVITATIONS_KEY = ['auth', 'company-invitations'] as const;

interface LoginResponse {
  accessToken: string;
  /** Jeton opaque de rafraîchissement — absent des flux qui n'ouvrent pas de session. */
  refreshToken?: string;
  user: IUser;
}

/* ── Auth ── */

export function useMe() {
  return useQuery<IUser>({
    queryKey: AUTH_KEY,
    queryFn: async () => {
      const { data } = await api.get<IUser>('/auth/me');
      return data;
    },
    enabled: !!getAccessToken(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Connexion par mot de passe. Quand la personne a un compte dans plusieurs
 * sociétés, l'API ne renvoie pas de session mais la liste des organisations
 * (`ILoginCompanySelection`) : l'écran la propose, puis rappelle avec `companyId`.
 */
export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { email: string; password: string; companyId?: string }) => {
      const { data } = await api.post<LoginResponse | ILoginCompanySelection>('/auth/login', dto);
      return data;
    },
    onSuccess: (data) => {
      if (isLoginCompanySelection(data)) return;
      setSession(data.accessToken, data.refreshToken);
      qc.setQueryData(AUTH_KEY, data.user);
    },
  });
}

/**
 * Lance la connexion via le portail Pool4ward pour l'organisation de la tuile :
 * l'API pose le `state` dans un cookie de ce navigateur et rend l'adresse
 * d'autorisation du portail qui le porte (cf. `SsoStartPage`).
 */
export function usePortalSsoStart() {
  return useMutation({
    mutationFn: async (dto: { organizationId: string }) => {
      const { data } = await api.post<{ authorizeUrl: string }>('/auth/sso/start', dto);
      return data.authorizeUrl;
    },
  });
}

/**
 * Connexion à partir du code SSO que le portail Pool4ward a mis dans l'URL de
 * retour, avec le `state` qui l'accompagne : l'API le compare à celui que ce
 * navigateur a reçu au lancement. Le code est à usage unique : l'API l'échange
 * côté serveur, on ne l'envoie donc qu'une fois (cf. `SsoCallbackPage`).
 */
export function usePortalSsoLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { code: string; state: string }) => {
      const { data } = await api.post<LoginResponse>('/auth/sso/callback', dto);
      return data;
    },
    onSuccess: (data) => {
      setSession(data.accessToken, data.refreshToken);
      qc.setQueryData(AUTH_KEY, data.user);
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: {
      email: string;
      password: string;
      name: string;
      companyName: string;
      companyCountry?: string;
    }) => {
      const { data } = await api.post<LoginResponse>('/auth/signup', dto);
      return data;
    },
    onSuccess: (data) => {
      setSession(data.accessToken, data.refreshToken);
      qc.setQueryData(AUTH_KEY, data.user);
    },
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { email: string; password: string; name: string; role?: string; companyId?: string }) => {
      const { data } = await api.post<LoginResponse>('/auth/register', dto);
      return data;
    },
    onSuccess: (data) => {
      setSession(data.accessToken, data.refreshToken);
      qc.setQueryData(AUTH_KEY, data.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    // Prévenir le serveur AVANT de purger : sans cet appel, le refresh token
    // resterait valide en base des semaines durant après la « déconnexion »,
    // et un jeton exfiltré du poste survivrait au geste censé le neutraliser.
    // Best-effort : une déconnexion ne doit jamais échouer côté client, on
    // purge et on redirige quoi qu'il arrive.
    try {
      await api.post('/auth/logout', { refreshToken: getRefreshToken() ?? undefined });
    } catch {
      // Réseau coupé ou session déjà invalide : sans importance ici.
    }
    clearSession();
    qc.clear();
    window.location.href = '/login';
  };
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { name?: string; preferences?: IUserPreferences }) => {
      const { data } = await api.patch<IUser>('/auth/me', dto);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(AUTH_KEY, data);
    },
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { name?: string; country?: string }) => {
      const { data } = await api.patch<ICompany>('/auth/company', dto);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: AUTH_KEY });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (dto: { currentPassword: string; newPassword: string }) => {
      const { data } = await api.patch('/auth/change-password', dto);
      return data;
    },
  });
}

/* ── Forgot / Reset password ── */

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (dto: { email: string }) => {
      const { data } = await api.post('/auth/forgot-password', dto);
      return data;
    },
  });
}

export function useValidateResetToken(token: string) {
  return useQuery<{ email: string }>({
    queryKey: ['reset-token', token],
    queryFn: async () => {
      const { data } = await api.get<{ email: string }>(`/auth/reset-password/${token}`);
      return data;
    },
    enabled: !!token,
    retry: false,
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (dto: { token: string; newPassword: string }) => {
      const { data } = await api.post('/auth/reset-password', dto);
      return data;
    },
  });
}

/* ── Invitation ── */

export function useInvitationByToken(token: string) {
  return useQuery<IInvitation>({
    queryKey: ['invitation', token],
    queryFn: async () => {
      const { data } = await api.get<IInvitation>(`/auth/invite/${token}`);
      return data;
    },
    enabled: !!token,
    retry: false,
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { token: string; name: string; password: string }) => {
      const { data } = await api.post<LoginResponse>('/auth/invite/accept', dto);
      return data;
    },
    onSuccess: (data) => {
      setSession(data.accessToken, data.refreshToken);
      qc.setQueryData(AUTH_KEY, data.user);
    },
  });
}

/* ── Company admin ── */

export function useCompanyUsers() {
  return useQuery<IUser[]>({
    queryKey: COMPANY_USERS_KEY,
    queryFn: async () => {
      const { data } = await api.get<IUser[]>('/auth/company/users');
      return data;
    },
  });
}

export function useCompanyInvitations() {
  return useQuery<IInvitation[]>({
    queryKey: COMPANY_INVITATIONS_KEY,
    queryFn: async () => {
      const { data } = await api.get<IInvitation[]>('/auth/company/invitations');
      return data;
    },
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { email: string; role?: string }) => {
      const { data } = await api.post<IInvitation>('/auth/company/invite', dto);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COMPANY_INVITATIONS_KEY });
    },
  });
}

export function useUpdateCompanyUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: { role?: string; name?: string; canQuote?: boolean; quoteRole?: string | null; moduleAccess?: Partial<Record<FeatureKey, boolean>> } }) => {
      const { data } = await api.patch<IUser>(`/auth/company/users/${id}`, dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANY_USERS_KEY }),
  });
}

export function useRemoveCompanyUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/auth/company/users/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANY_USERS_KEY }),
  });
}

/* ── SuperAdmin ── */

export function useUsers() {
  return useQuery<IUser[]>({
    queryKey: USERS_KEY,
    queryFn: async () => {
      const { data } = await api.get<IUser[]>('/auth/users');
      return data;
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: { role?: string; name?: string; companyId?: string; isActive?: boolean } }) => {
      const { data } = await api.patch<IUser>(`/auth/users/${id}`, dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

/** Lie (ou, avec `null`, délie) une personne à son compte du portail Pool4ward. */
export function useLinkUserToPortal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, portalUserId }: { id: string; portalUserId: string | null }) => {
      const { data } = await api.put<IUser>(`/auth/users/${id}/portal-link`, { portalUserId });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

/** Lie (ou, avec `null`, délie) une société à son organisation du portail Pool4ward. */
export function useLinkCompanyToPortal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, portalOrganizationId }: { id: string; portalOrganizationId: string | null }) => {
      const { data } = await api.put<ICompany>(`/auth/companies/${id}/portal-link`, { portalOrganizationId });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_KEY }),
  });
}

export function useCreateUserByAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { email: string; password: string; name: string; role?: string; companyId?: string }) => {
      const { data } = await api.post<LoginResponse>('/auth/register', dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useCompanies() {
  return useQuery<ICompany[]>({
    queryKey: COMPANIES_KEY,
    queryFn: async () => {
      const { data } = await api.get<ICompany[]>('/auth/companies');
      return data;
    },
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { name: string; country?: string }) => {
      const { data } = await api.post<ICompany>('/auth/companies', dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_KEY }),
  });
}

export function useUpdateCompanyFeatures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, features }: {
      id: string;
      features: Partial<ICompanyFeatures>;
    }) => {
      const { data } = await api.patch<ICompany>(`/auth/companies/${id}/features`, features);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COMPANIES_KEY });
      // Refresh /auth/me too in case the superadmin is toggling their own
      // company's features (live UI update without re-login).
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useStats() {
  return useQuery<{
    totalUsers: number;
    totalCompanies: number;
    totalInvitations: number;
    pendingInvitations: number;
  }>({
    queryKey: ['auth', 'stats'],
    queryFn: async () => {
      const { data } = await api.get('/auth/stats');
      return data;
    },
  });
}
